import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, Divider, Theme, withStyles, WithStyles } from '@material-ui/core';
import {
  DateRange as DateIcon,
  Domain as AddressIcon,
  Person as PersonIcon,
  Videocam as VideoIcon,
} from '@material-ui/icons';
import classnames from 'classnames';
import { get } from 'lodash';
import moment from 'moment';
import { Appointment } from 'requests/resources/appointment';

import { Address, Availability } from '@lib/resources/types';

import { useAddress } from 'hooks';

import Typography from './Typography';
import TypographyWithIcon from './TypographyWithIcon';

const messages = defineMessages({
  ApptDetails: {
    id: 'components.v2.CompactAppointmentDetails.ApptDetails',
    defaultMessage: 'Appointment Details',
  },
  providerName: {
    id: 'components.v2.CompactAppointmentDetails.providerName',
    defaultMessage: '{name}',
  },
  telehealthAddress: {
    id: 'components.v2.CompactAppointmentDetails.telehealthAddress',
    defaultMessage: 'Telehealth - Online appointment',
  },
});

const styles = ({ spacing: { unit }, custom, typography }: Theme) =>
  createStyles({
    infoItem: {
      marginBottom: unit,
    },
    marginBotton: {
      marginBottom: unit * 2,
    },
    icon: {
      color: `${custom.palette.colorSupportSlate}`,
      fontSize: typography.pxToRem(16),
    },
    iconMultipleOnLines: {
      marginTop: unit / 4,
      alignSelf: 'start',
    },
  });

export interface CompactAppointmentDetailsProps extends WithStyles<typeof styles> {
  appointment: Appointment | Availability;
}

const CompactAppointmentDetails: React.FC<CompactAppointmentDetailsProps> = ({ appointment, classes }) => {
  const { formatMessage } = useIntl();

  const address = useAddress(appointment && appointment.facility ? (appointment.facility as Address) : {});
  const isTelehealth =
    get(appointment, 'type.modality') === 'telehealth' || get(appointment, 'appointmentType.modality') === 'telehealth';

  return (
    <>
      <Divider variant="fullWidth" className={classes.marginBotton} />
      <div className={classes.infoItem}>
        <TypographyWithIcon
          icon={<DateIcon className={classnames(classes.icon, classes.iconMultipleOnLines)} />}
          variant="supportW400F14"
        >
          {moment(get(appointment, 'date') as string).format('LLLL')}
        </TypographyWithIcon>
      </div>
      <div className={classes.infoItem}>
        <TypographyWithIcon icon={<PersonIcon className={classes.icon} />} variant="supportW400F14">
          {formatMessage(messages.providerName, { name: get(appointment, 'provider.name', '') })}
        </TypographyWithIcon>
      </div>
      <div className={classes.infoItem}>
        <TypographyWithIcon
          icon={
            isTelehealth ? (
              <VideoIcon className={classnames(classes.icon, classes.iconMultipleOnLines)} />
            ) : (
              <AddressIcon className={classnames(classes.icon, classes.iconMultipleOnLines)} />
            )
          }
          variant="supportW400F14"
        >
          {!isTelehealth && get(appointment, 'facility.name', '')}
          <Typography variant="supportW400F14">
            {isTelehealth ? formatMessage(messages.telehealthAddress) : address}
          </Typography>
          {!isTelehealth && <Typography variant="supportW400F14">{get(appointment, 'facility.phone', '')}</Typography>}
        </TypographyWithIcon>
      </div>
    </>
  );
};

export default withStyles(styles)(CompactAppointmentDetails);
